import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import SearchField from './search/SearchField';
import Button from './ui/Button';
import PeopleConnectionRow from './people/PeopleConnectionRow';
import DiscoverPeopleSection from './people/DiscoverPeopleSection';
import PeopleListEmptyState from './people/PeopleListEmptyState';
import { peopleListSurfaceClass } from '../config/glassSurfaces';
import { NOTIFICATION_TYPES } from '../utils/notifications';
import { filterPeopleByName } from '../utils/peopleSearchFilter';
import { isSearchInFlight, shouldShowSearchEmpty } from '../utils/searchUiState';
import { invalidateAfterFollow } from '../lib/query/invalidateMutations';
import { usePromptAuth } from './auth/AuthPrompt';
import {
  useFollowersForUser,
  useFollowingForUser,
  useSearchPeopleRanked,
} from '../hooks/queries/usePeopleQueries';

const LIST_SKELETON_ROWS = 8;

function ConnectionListSkeleton() {
  return (
    <div className={peopleListSurfaceClass()} aria-hidden>
      {Array.from({ length: LIST_SKELETON_ROWS }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-3 px-2 py-2.5 animate-pulse min-h-[52px]"
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="h-9 w-9 rounded-full flex-shrink-0 skeleton-shimmer" />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="h-3.5 w-2/5 max-w-[140px] rounded skeleton-shimmer" />
              <div className="h-3 w-24 rounded skeleton-shimmer" />
            </div>
          </div>
          <div className="h-7 w-16 rounded-md skeleton-shimmer shrink-0" />
        </div>
      ))}
    </div>
  );
}

export default function FollowConnections({ tab }) {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const queryClient = useQueryClient();
  const { userId: paramUserId } = useParams();
  const { session, authReady } = useCurrentUserProfile();
  const currentUserId = session?.user?.id;
  const profileUserId = paramUserId || currentUserId;
  const peopleQueriesEnabled = authReady && Boolean(currentUserId);
  const isOwnProfile = profileUserId === currentUserId;
  const showDiscover = tab === 'following' && isOwnProfile;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [followLoadingId, setFollowLoadingId] = useState(null);
  const [optimisticFollowing, setOptimisticFollowing] = useState(new Set());

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const title = tab === 'followers' ? 'Followers' : 'Following';
  const isSearching = Boolean(debouncedSearchQuery);

  const { data: myFollowing = [] } = useFollowingForUser(currentUserId, {
    enabled: peopleQueriesEnabled,
  });
  const followingIds = useMemo(() => new Set(myFollowing.map((p) => p.id)), [myFollowing]);

  const followersQuery = useFollowersForUser(profileUserId, {
    enabled: authReady && tab === 'followers' && Boolean(profileUserId) && !isSearching,
  });
  const followingQuery = useFollowingForUser(profileUserId, {
    enabled: authReady && tab === 'following' && Boolean(profileUserId) && !isSearching,
  });

  const searchQueryResult = useSearchPeopleRanked(currentUserId, debouncedSearchQuery, {
    enabled: peopleQueriesEnabled && isSearching,
  });

  const activeQuery = tab === 'followers' ? followersQuery : followingQuery;
  const list = activeQuery.data ?? [];
  const loading = !isSearching && activeQuery.isPending && list.length === 0;

  const searchResults = useMemo(() => {
    const raw = searchQueryResult.data ?? [];
    return filterPeopleByName(raw, debouncedSearchQuery);
  }, [searchQueryResult.data, debouncedSearchQuery]);
  const searchInFlight = isSearchInFlight({
    input: searchQuery,
    debouncedQuery: debouncedSearchQuery,
    isLoading: searchQueryResult.isPending,
    isFetching: searchQueryResult.isFetching,
  });
  const showSearchEmpty = shouldShowSearchEmpty({
    input: searchQuery,
    debouncedQuery: debouncedSearchQuery,
    isLoading: searchQueryResult.isPending,
    isFetching: searchQueryResult.isFetching,
    resultCount: searchResults.length,
  });

  const handleFollow = async (userId) => {
    if (!currentUserId || followLoadingId === userId || userId === currentUserId) return;

    setOptimisticFollowing((prev) => new Set([...prev, userId]));
    setFollowLoadingId(userId);

    try {
      const { error } = await supabase
        .from('follows')
        .insert({ follower_id: currentUserId, following_id: userId });

      if (error) throw error;

      const { data: currentUserProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', currentUserId)
        .single();

      const displayName = currentUserProfile?.display_name || 'Someone';

      await supabase.from('notifications').insert({
        user_id: userId,
        type: NOTIFICATION_TYPES.FOLLOW,
        title: 'New follower',
        message: `${displayName} started following you`,
        related_user_id: currentUserId,
        read: false,
        created_at: new Date().toISOString(),
      });
      invalidateAfterFollow(queryClient, { viewerId: currentUserId, targetUserId: userId });
    } catch (err) {
      console.error('[FollowConnections] follow failed:', err);
    } finally {
      setFollowLoadingId(null);
      setOptimisticFollowing((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const renderPersonRow = (person) => {
    const isSelf = person.id === currentUserId;
    const isFollowing = optimisticFollowing.has(person.id) || followingIds.has(person.id);
    return (
      <PeopleConnectionRow
        key={person.id}
        profile={person}
        isFollowing={isFollowing}
        isLoading={followLoadingId === person.id}
        onFollow={() => handleFollow(person.id)}
        onNavigate={() => navigate(`/profile/${person.id}`)}
        showFollowActions={!isSelf}
      />
    );
  };

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="mb-6 text-gray-500 dark:text-dark-muted">
          Log in to view {title.toLowerCase()}.
        </p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log In
        </Button>
      </div>
    );
  }

  if (!profileUserId) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center text-gray-500 dark:text-dark-muted">
        Profile not found.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-dark-text">{title}</h1>

      <div className="mb-4">
        <SearchField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={isSearching ? 'Search people...' : `Search ${title.toLowerCase()}...`}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {isSearching ? (
        <>
          {searchInFlight && searchResults.length === 0 ? (
            <ConnectionListSkeleton />
          ) : showSearchEmpty ? (
            <PeopleListEmptyState>
              {`No people found matching "${debouncedSearchQuery}"`}
            </PeopleListEmptyState>
          ) : (
            <div className={peopleListSurfaceClass()}>
              {searchResults.map((person) => renderPersonRow(person))}
            </div>
          )}
        </>
      ) : (
        <>
          {showDiscover && (
            <DiscoverPeopleSection
              currentUserId={currentUserId}
              enabled={peopleQueriesEnabled}
              followingIds={followingIds}
              optimisticFollowing={optimisticFollowing}
              followLoadingId={followLoadingId}
              onFollow={handleFollow}
              onNavigate={(userId) => navigate(`/profile/${userId}`)}
            />
          )}

          {loading ? (
            <ConnectionListSkeleton />
          ) : list.length === 0 ? (
            <p className="rounded-xl bg-gray-50 py-10 text-center text-gray-500 dark:bg-dark-card dark:text-dark-muted">
              {`No ${title.toLowerCase()} yet.`}
            </p>
          ) : (
            <div className={peopleListSurfaceClass()}>
              {list.map((person) => renderPersonRow(person))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
